import { useEffect, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Textarea, Spinner, EmptyState, Pill, fmtEur } from "@/components/common";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, Landmark, Download } from "lucide-react";

const CAT_ENTRATE = ["Quote soci", "Iscrizioni", "Corsi", "Sponsorizzazioni", "Contributi", "Altre entrate"];
const CAT_USCITE = ["Affitto impianto", "Attrezzatura", "Assicurazioni", "Compensi", "Utenze", "Trasferte", "Altre spese"];

export default function PrimaNota() {
  const { can } = useAuth();
  const editable = can("contabilita", "edit");
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo: "entrata", categoria: "Quote soci", importo: 0, data: new Date().toISOString().slice(0, 10), descrizione: "", metodo: "cassa" });
  const [filtro, setFiltro] = useState("");

  const load = () => {
    api.get("/movimenti").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/contabilita/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await api.post("/movimenti", { ...form, importo: Number(form.importo) }); toast.success("Movimento registrato"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/movimenti/${id}`); load(); };
  const exportCsv = () => {
    const rows = [["Data", "Tipo", "Categoria", "Descrizione", "Metodo", "Importo"], ...items.map((m) => [m.data, m.tipo, m.categoria, m.descrizione, m.metodo, m.importo])];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "prima_nota.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const downloadPdf = () => {
    const token = localStorage.getItem("asd_token");
    window.open(`${API}/contabilita/report.pdf?auth=${token}`, "_blank");
  };

  if (!items) return <Spinner />;
  const filtered = filtro ? items.filter((m) => m.tipo === filtro) : items;

  return (
    <div>
      <PageHeader title="Cassa e Prima Nota" subtitle="Entrate, uscite e situazione economica"
        actions={<>
          <Button variant="outline" onClick={downloadPdf} data-testid="report-pdf-btn"><Download size={15} /> Report PDF</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-commercialista-btn"><Download size={15} /> Export CSV</Button>
          {editable && <Button onClick={() => setOpen(true)} data-testid="add-movimento-btn"><Plus size={16} /> Movimento</Button>}
        </>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[["Entrate", summary.entrate, TrendingUp, "text-emerald-600"], ["Uscite", summary.uscite, TrendingDown, "text-red-500"], ["Saldo", summary.saldo, Wallet, "text-slate-900"], ["Cassa", summary.cassa, Landmark, "text-sky-600"]].map(([l, v, Ic, cls]) => (
          <Card key={l} className="p-4" data-testid={`conta-${l.toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-1"><Ic size={16} className={cls} /><span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{l}</span></div>
            <p className={`text-xl font-bold font-display ${cls}`}>{fmtEur(v)}</p>
          </Card>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        {[["", "Tutti"], ["entrata", "Entrate"], ["uscita", "Uscite"]].map(([v, l]) => (
          <button key={v} onClick={() => setFiltro(v)} data-testid={`filter-${v || "all"}`} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtro === v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={Wallet} title="Nessun movimento" /></Card> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">{["Data", "Categoria", "Descrizione", "Metodo", "Importo", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}</tr></thead>
          <tbody>{filtered.map((m) => (
            <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`movimento-row-${m.id}`}>
              <td className="px-4 py-3 text-slate-500">{m.data}</td>
              <td className="px-4 py-3"><Pill className={m.tipo === "entrata" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{m.categoria}</Pill></td>
              <td className="px-4 py-3 text-slate-600">{m.descrizione || "—"}</td>
              <td className="px-4 py-3 text-slate-500 capitalize">{m.metodo}</td>
              <td className={`px-4 py-3 font-semibold ${m.tipo === "entrata" ? "text-emerald-600" : "text-red-500"}`}>{m.tipo === "entrata" ? "+" : "−"}{fmtEur(m.importo)}</td>
              <td className="px-4 py-3 text-right">{editable && <button onClick={() => del(m.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>}</td>
            </tr>
          ))}</tbody>
        </table></div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Nuovo movimento">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Select label="Tipo" value={form.tipo} onChange={(e) => { const t = e.target.value; setForm((f) => ({ ...f, tipo: t, categoria: (t === "entrata" ? CAT_ENTRATE : CAT_USCITE)[0] })); }} data-testid="mov-tipo">
              <option value="entrata">Entrata</option><option value="uscita">Uscita</option>
            </Select>
            <Select label="Categoria" value={form.categoria} onChange={(e) => upd("categoria", e.target.value)}>
              {(form.tipo === "entrata" ? CAT_ENTRATE : CAT_USCITE).map((c) => <option key={c}>{c}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Importo €" type="number" data-testid="mov-importo" value={form.importo} onChange={(e) => upd("importo", e.target.value)} />
            <Input label="Data" type="date" value={form.data} onChange={(e) => upd("data", e.target.value)} />
          </div>
          <Select label="Metodo" value={form.metodo} onChange={(e) => upd("metodo", e.target.value)}><option value="cassa">Cassa</option><option value="banca">Banca</option></Select>
          <Textarea label="Descrizione" rows={2} value={form.descrizione} onChange={(e) => upd("descrizione", e.target.value)} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-movimento-btn">Registra</Button></div>
        </div>
      </Modal>
    </div>
  );
}
